<!-- Loading skeleton (same shape as the card) until business/details arrives -->
<div class="mc-card mc-skeleton" *ngIf="!business" aria-busy="true" aria-label="Loading company details">
  <div class="sk sk-logo"></div>
  <div class="sk sk-line sk-name"></div>
  <div class="sk sk-line sk-tagline"></div>
  <div class="sk-row" *ngFor="let w of [70, 55, 45, 65, 60, 50, 75]">
    <div class="sk sk-icon"></div>
    <div class="sk sk-line" [style.width.%]="w"></div>
  </div>
  <div class="sk sk-button"></div>
  <div class="sk sk-roles"></div>
</div>

<div class="mc-card" *ngIf="business">

  <!-- Logo + name + tagline -->
  <div class="mc-logo">
    <img [src]="logoSrc" alt="" onerror="this.src='assets/images/gallery/company.png';">
  </div>
  <div class="mc-name" [title]="business.businessName">{{ business.businessName }}</div>
  <div class="mc-tagline" *ngIf="business.tagLine">{{ business.tagLine }}</div>

  <!-- Key facts (each row only when there's data) -->
  <div class="mc-info-row" *ngIf="business.industryClassification">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
    <span>{{ business.industryClassification }}</span>
  </div>
  <div class="mc-info-row" *ngIf="business.companySize">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
    <span>{{ business.companySize }} employees</span>
  </div>
  <div class="mc-info-row" *ngIf="business.yearStarted">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
    <span>Founded {{ business.yearStarted }}</span>
  </div>
  <div class="mc-info-row" *ngIf="locationText">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
    <span>{{ locationText }}</span>
  </div>
  <div class="mc-info-row" *ngIf="location?.website">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10z"/></svg>
    <a [href]="withProtocol(location.website)" target="_blank" rel="noopener noreferrer" [title]="location.website">{{ location.website }}</a>
  </div>
  <div class="mc-info-row" *ngIf="phone">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6z"/></svg>
    <span>{{ phone }}</span>
  </div>
  <div class="mc-info-row" *ngIf="email">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
    <a [href]="'mailto:' + email" [title]="email">{{ email }}</a>
  </div>

  <!-- Follow / Unfollow (same rules + actions as the banner buttons) -->
  <button type="button" class="mc-follow-btn" *ngIf="canFollow || isPreview" (click)="follow.emit()"
    [disabled]="isPreview" [attr.title]="isPreview ? 'Preview only' : null">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
    <span>Follow {{ business.businessName }}</span>
  </button>
  <button type="button" class="mc-follow-btn mc-follow-btn--outline" *ngIf="canUnfollow" (click)="unfollow.emit()">
    <span>Unfollow</span>
  </button>

  <!-- Open roles -->
  <a class="mc-open-roles" *ngIf="openRoles > 0" (click)="viewJobs.emit()" role="button" tabindex="0"
    (keydown.enter)="viewJobs.emit()">
    <div>
      <div class="mc-open-roles-count">{{ openRoles }}</div>
      <div class="mc-open-roles-sub">open {{ openRoles === 1 ? 'role' : 'roles' }}</div>
    </div>
    <div class="mc-open-roles-label">View Jobs &rarr;</div>
  </a>

  <!-- Social links -->
  <ng-container *ngIf="socials.length">
    <div class="mc-social-title">Connect with us</div>
    <div class="mc-social-icons">
      <a *ngFor="let s of socials; trackBy: trackByKey" [href]="s.url" target="_blank" rel="noopener noreferrer"
        class="mc-social-icon mc-social-icon--{{ s.key }}" [attr.aria-label]="s.label" [title]="s.label">
        <svg *ngIf="s.key === 'linkedin'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
        <svg *ngIf="s.key === 'twitter'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231z"/></svg>
        <svg *ngIf="s.key === 'youtube'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>
        <svg *ngIf="s.key === 'instagram'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        <svg *ngIf="s.key === 'facebook'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        <svg *ngIf="s.key === 'tiktok'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M21 7.917v4.034a9.948 9.948 0 0 1-5-1.951v4.5a6.5 6.5 0 1 1-8-6.326v4.326a2.5 2.5 0 1 0 4 2v-11.5h4.083a6.005 6.005 0 0 0 4.917 4.917z"/></svg>
      </a>
    </div>
  </ng-container>

  <!-- Share (always; also enabled in Member View preview) + Message -->
  <div class="mc-share-row">
    <button type="button" class="mc-share-btn" (click)="shareBusiness()" [disabled]="!business?.businessId">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
      {{ shareCopied ? 'Link copied' : 'Share' }}
    </button>
    <button *ngIf="canMessage || isPreview" type="button" class="mc-share-btn" (click)="message.emit()"
      [disabled]="isPreview" [attr.title]="isPreview ? 'Preview only' : null">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      Message
    </button>
  </div>

</div>
